/**
 * Shared toolbar for list pages (Product Library, Registrations, Claims, etc.).
 *
 * Both layouts start with the same views row (WPS-276 rework): one view
 * picker, then what can be done with the list's changes (Save, Save as new
 * view, Discard). However many views someone keeps, they take one place.
 *
 * - "classic" (default): views row + controls row. Used by Products Library, Partnerships.
 * - "compact": views row + StatusPills (left) | Search + Columns + Export (right).
 *   Used by Registrations, Claims, and future entity pages following Clara Vale V2.
 */

import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { SearchInput } from '@/shared/components/ui/search-input'
import {
  ViewSwitcher,
  ColumnToggle,
  FilterBar,
  type DataTableColumn,
  type ActiveFilter,
} from '@/admin/components/data-table'
import { OpenViewBar, UnsavedViewDialog } from '@/admin/components/data-table/view-switcher'
import type { TableView, TableViewConfig } from '@/admin/types/table-views'
import { StatusPills, type StatusPillItem } from '@/admin/components/shared/status-pills'
import {
  ProductFilterMenu,
  type ProductFilterFacet,
  type ProductFilterValue,
} from '@/shared/components/ui/product-filter-menu'
import { ExportMenu } from '@/admin/components/shared/export-menu'
import type { ExportScope } from '@/admin/lib/export-scope'

interface ListPageToolbarProps<T> {
  /** Unique table identifier (e.g. 'products', 'registrations') */
  tableId: string

  /** Layout mode - "classic" (default) or "compact" (Clara Vale V2) */
  layout?: 'classic' | 'compact'

  // Search
  search: string
  onSearchChange: (value: string) => void
  searchPlaceholder?: string

  // Views
  views: TableView[]
  activeViewUuid: string | null
  onSelectView: (view: TableView | null) => void
  onSaveView: (name: string, config: TableViewConfig) => void
  onDeleteView: (uuid: string) => void
  onRenameView: (uuid: string, name: string) => void
  onSetOrgDefault?: (config: TableViewConfig) => void
  currentConfig: TableViewConfig
  /** The open view has changes not saved to it (see `useSavedListView`). */
  activeViewModified?: boolean
  /** Others see the open view, so saving into it is confirmed first. */
  activeViewSeenByOthers?: boolean
  isSavingActiveView?: boolean
  /** Saves the list as shown into the open view. */
  onSaveActiveView?: () => void
  /** Puts the open view back as it was saved. */
  onDiscardChanges?: () => void
  /** No view is open and the list has been set up (filters, status, labels). */
  listSetUp?: boolean
  /** Clears the set-up list back to a plain one (no view open). */
  onClearListSetUp?: () => void
  /** Whether the user may rename or delete a view (see `ViewSwitcher`). */
  canEditView?: (view: TableView) => boolean
  canManageDefaults?: boolean
  /**
   * Show the saved-views switcher (incl. the "save current view" input).
   * Defaults to true. Set false on surfaces that don't own per-table named
   * views - e.g. dashboard tiles, which get their views from the dashboard's
   * own role-level system and auto-persist column/filter prefs, so a per-tile
   * "save view" control would be redundant (and dead when unwired).
   */
  showViews?: boolean

  // Filters & columns
  columns: DataTableColumn<T>[]
  activeFilters: ActiveFilter[]
  onFiltersChange: (filters: ActiveFilter[]) => void
  /**
   * Show the generic column-filter (FILTER) button. Defaults to true. The
   * product library sets this false because its variant Filters fly-out is the
   * filtering surface there and two adjacent filter buttons read as redundant.
   */
  showColumnFilter?: boolean
  /**
   * Show the Columns toggle. Defaults to true. Set false on surfaces whose
   * column set is fixed (e.g. Inspections) so no Columns button renders.
   */
  showColumnToggle?: boolean
  columnVisibility: Record<string, boolean>
  onColumnVisibilityChange: (visibility: Record<string, boolean>) => void

  /**
   * Record-metadata filter fly-out (compact layout only). When facets are
   * provided, a Filters button renders immediately to the right of Columns.
   * Facets come from the list endpoint's ``filter_options`` block; the
   * selection is a map of facet key -> selected option values, which the page
   * merges into its query params. Empty/undefined facets render nothing.
   */
  filterFacets?: ProductFilterFacet[]
  filterValues?: ProductFilterValue
  onFilterValuesChange?: (values: ProductFilterValue) => void

  /** Column IDs that should be locked visible in the column picker */
  alwaysShownColumns?: string[]

  /** Column order - array of column ids for drag-and-drop reordering */
  columnOrder?: string[]
  onColumnOrderChange?: (order: string[]) => void

  /**
   * CSV export handler - when provided, shows a download button that opens a
   * scope picker (Current view / period presets / All time). The chosen
   * ``ExportScope`` is passed to the handler.
   */
  onExport?: (scope: ExportScope) => void
  isExporting?: boolean

  /** Extra elements rendered after Columns toggle (e.g. list/grid toggle) */
  trailingControls?: ReactNode

  // Compact layout only - status pills
  statusPills?: StatusPillItem[]
  activeStatusKey?: string | null
  onStatusSelect?: (key: string | null) => void
  statusTotalCount?: number
  /** Status-pill keys the user has dismissed - filtered out of the rendered pills. */
  dismissedStatusKeys?: string[]
  /** When provided, status pills show a dismiss × that calls this with the pill key. */
  onStatusDismiss?: (key: string) => void
}

export function ListPageToolbar<T>({
  layout = 'classic',
  search,
  onSearchChange,
  searchPlaceholder = 'Search...',
  views,
  activeViewUuid,
  onSelectView,
  onSaveView,
  onDeleteView,
  activeViewModified,
  activeViewSeenByOthers = false,
  isSavingActiveView = false,
  onSaveActiveView,
  onDiscardChanges,
  listSetUp = false,
  onClearListSetUp,
  canEditView,
  onRenameView,
  onSetOrgDefault,
  currentConfig,
  canManageDefaults = false,
  showViews = true,
  columns,
  activeFilters,
  onFiltersChange,
  showColumnFilter = true,
  showColumnToggle = true,
  filterFacets,
  filterValues,
  onFilterValuesChange,
  columnVisibility,
  onColumnVisibilityChange,
  alwaysShownColumns,
  columnOrder,
  onColumnOrderChange,
  onExport,
  isExporting,
  trailingControls,
  statusPills,
  activeStatusKey,
  onStatusSelect,
  statusTotalCount,
  dismissedStatusKeys,
  onStatusDismiss,
}: ListPageToolbarProps<T>) {
  const { t } = useTranslation()
  // Bumped by the bar's "Save as new view" to open the switcher's name box.
  const [namingRequest, setNamingRequest] = useState(0)
  // Leaving a view with unsaved changes waits here for Save / Discard / Cancel.
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null)
  const activeView = showViews ? (views.find((v) => v.uuid === activeViewUuid) ?? null) : null
  const canSaveActive =
    !!activeView && (canEditView ? canEditView(activeView) : true) && !!onSaveActiveView

  /** Runs `leave` now, or first asks what to do with the open view's changes. */
  function leaveView(leave: () => void) {
    if (activeView && activeViewModified) setPendingLeave(() => leave)
    else leave()
  }
  const selectView = (view: TableView | null) => {
    if (view?.uuid === activeViewUuid) return
    leaveView(() => onSelectView(view))
  }

  // A list's toolbar (views or status pills) sits in one panel, styled like
  // the record top panel on a claim, so views, statuses and tools read as one
  // tidy block. A bare control strip (a dashboard card's header) stays bare.
  const framed = showViews || !!statusPills
  const panel = framed
    ? 'cv-data-table relative z-20 rounded-xl border border-[var(--panel-rule)] bg-[var(--panel)] px-4 py-3 md:px-5'
    : 'cv-data-table relative z-20'
  const panelRule = 'border-b-[0.5px] border-b-[var(--panel-rule)]'

  const viewsRow = showViews ? (
    <div className="cv-data-table flex flex-wrap items-center gap-x-3 gap-y-2">
      <ViewSwitcher
        views={views}
        activeViewUuid={activeViewUuid}
        onSelectView={selectView}
        onSaveView={onSaveView}
        onDeleteView={onDeleteView}
        activeViewModified={activeViewModified}
        namingRequest={namingRequest}
        canEditView={canEditView}
        onRenameView={onRenameView}
        onSetOrgDefault={onSetOrgDefault}
        currentConfig={currentConfig}
        canManageDefaults={canManageDefaults}
      />
      {activeView ? (
        <OpenViewBar
          viewName={activeView.name}
          nameShown
          modified={!!activeViewModified}
          canSave={canSaveActive}
          seenByOthers={activeViewSeenByOthers}
          isOrgDefault={activeView.is_org_default}
          saving={isSavingActiveView}
          onSave={() => onSaveActiveView?.()}
          onSaveAsNew={() => setNamingRequest((n) => n + 1)}
          onDiscard={() => onDiscardChanges?.()}
        />
      ) : (
        <OpenViewBar
          viewName={null}
          nameShown
          modified={listSetUp}
          canSave={false}
          noViewNotice={t(
            'viewSwitcher.listSetUpNotice',
            'This list is set up but not saved as a view.',
          )}
          discardLabel={t('viewSwitcher.clearFilters', 'Clear filters')}
          onSave={() => {}}
          onSaveAsNew={() => setNamingRequest((n) => n + 1)}
          onDiscard={() => onClearListSetUp?.()}
        />
      )}
      {activeView && pendingLeave && (
        <UnsavedViewDialog
          open
          viewName={activeView.name}
          canSave={canSaveActive}
          seenByOthers={activeViewSeenByOthers}
          onCancel={() => setPendingLeave(null)}
          onDiscard={() => {
            const leave = pendingLeave
            setPendingLeave(null)
            leave()
          }}
          onSave={() => {
            const leave = pendingLeave
            setPendingLeave(null)
            onSaveActiveView?.()
            leave()
          }}
          onSaveAsNew={() => {
            setPendingLeave(null)
            setNamingRequest((n) => n + 1)
          }}
        />
      )}
    </div>
  ) : null

  if (layout === 'compact') {
    const visibleStatusPills = dismissedStatusKeys?.length
      ? (statusPills ?? []).filter((pill) => !dismissedStatusKeys.includes(pill.key))
      : statusPills
    return (
      <div
        className={`${panel} mb-5 grid grid-cols-1 items-start gap-2 md:grid-cols-[minmax(0,1fr)_auto] md:gap-3`}
      >
        {viewsRow && <div className={`${panelRule} pb-3 md:col-span-2`}>{viewsRow}</div>}
        {/* Left: Status pills - wrap freely across rows */}
        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          {visibleStatusPills && onStatusSelect && (
            <StatusPills
              items={visibleStatusPills}
              activeKey={activeStatusKey ?? null}
              onSelect={(key) => {
                // ALL is the list's home: it closes an open view rather than
                // saving "all statuses" into it (asking first if it changed).
                if (key === null && activeView) {
                  leaveView(() => {
                    onSelectView(null)
                    onStatusSelect(null)
                  })
                  return
                }
                onStatusSelect(key)
              }}
              totalCount={statusTotalCount ?? 0}
              onDismiss={onStatusDismiss}
            />
          )}
        </div>
        {/* Right: Columns + Search + Export - stays pinned to the top-right */}
        <div className="flex items-center gap-2 shrink-0 md:justify-self-end">
          {showColumnToggle && (
            <ColumnToggle
              columns={columns}
              visibility={columnVisibility}
              onVisibilityChange={onColumnVisibilityChange}
              alwaysShownColumns={alwaysShownColumns}
              columnOrder={columnOrder}
              onColumnOrderChange={onColumnOrderChange}
            />
          )}
          {filterFacets && filterValues && onFilterValuesChange && (
            <ProductFilterMenu
              facets={filterFacets}
              value={filterValues}
              onChange={onFilterValuesChange}
              triggerClassName="h-[28px] rounded-[4px] border-[0.5px] border-[var(--rule-strong)] px-2.5 text-[12px]"
            />
          )}
          <SearchInput
            placeholder={searchPlaceholder}
            value={search}
            onChange={onSearchChange}
            iconSize={12}
            className="cv-data-table-body w-[120px] rounded-[2px] border-[0.5px] border-solid border-[var(--rule-strong)] bg-[var(--bg)] py-[5px] pr-2.5 text-[var(--ink)] outline-none transition-[border-color] duration-150 focus:border-[var(--green)] md:w-[200px]"
          />
          {onExport && (
            <ExportMenu
              layout="compact"
              onExport={onExport}
              isExporting={isExporting}
              triggerClassName="h-[28px] w-[30px] rounded-[4px] border-[0.5px] border-solid border-[var(--rule-strong)] bg-transparent p-0 text-[var(--ink-3)] transition-all duration-150 hover:border-[var(--rule-heavy)] hover:bg-transparent hover:text-[var(--ink-2)]"
            />
          )}
          {trailingControls}
        </div>
      </div>
    );
  }

  // Classic layout: views row over the controls row, in the same panel.
  return (
    <div className={`${panel} mb-6`}>
      {viewsRow && <div className={`${panelRule} mb-3 pb-3`}>{viewsRow}</div>}
      {/* Controls row - Search | spacer | Filter + Columns + extras */}
      <div className="cv-data-table flex flex-wrap items-center gap-3">
        <div className="w-full md:max-w-xs">
          <SearchInput
            placeholder={searchPlaceholder}
            value={search}
            onChange={onSearchChange}
            className="cv-data-table-body"
          />
        </div>

        <div className="flex-1" />

        {showColumnFilter && (
          <FilterBar
            columns={columns}
            activeFilters={activeFilters}
            onFiltersChange={onFiltersChange}
          />
        )}

        {showColumnToggle && (
          <ColumnToggle
            columns={columns}
            visibility={columnVisibility}
            onVisibilityChange={onColumnVisibilityChange}
            alwaysShownColumns={alwaysShownColumns}
          />
        )}

        {onExport && (
          <ExportMenu
            layout="classic"
            onExport={onExport}
            isExporting={isExporting}
            triggerClassName="cv-data-table-compact rounded-[6px] border-[0.5px] border-solid border-[var(--rule-strong)] px-2.5 py-1.5 uppercase tracking-[0.1em] text-[var(--ink-2)] transition-colors duration-150 hover:border-[var(--ink-3)]"
          />
        )}

        {trailingControls}
      </div>
    </div>
  );
}
